(function () {
  'use strict';

  var instances = [];
  var DEBOUNCE_MS = 250;

  var HEADER_FORM_SELECTORS = [
    '.oddit-header__search',
    '.oddit-header__mobile-menu-search'
  ];
  var HEADER_INPUT_SELECTORS = [
    '.oddit-header__search-input',
    '.oddit-header__mobile-menu-search-input'
  ];

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function createDrawer(drawer) {
    if (!drawer || drawer.__odditSearchInit) return null;
    drawer.__odditSearchInit = true;

    var lastFocused = null;
    var isOpen = drawer.classList.contains('is-open');
    var input = drawer.querySelector('[data-search-input]');
    var overlay = drawer.querySelector('[data-search-overlay]');
    var closeBtn = drawer.querySelector('[data-search-close]');

    var resourceTypes = drawer.getAttribute('data-search-resource-types') || 'product';
    var resultsLimit = parseInt(drawer.getAttribute('data-search-results-limit'), 10) || 4;

    var resultsLabel = drawer.querySelector('[data-search-results-label]');
    var queryEl = drawer.querySelector('[data-search-query]');
    var loadingEl = drawer.querySelector('[data-search-loading]');
    var productsEl = drawer.querySelector('[data-search-products]');
    var emptyEl = drawer.querySelector('[data-search-empty]');
    var idleEl = drawer.querySelector('[data-search-idle]');

    // Reparenting plumbing: on mobile the body element travels into the mobile
    // menu drawer's host. Capture its original home so we can restore it.
    var bodyEl = drawer.querySelector('.oddit-search-drawer__body');
    var bodyHomeParent = bodyEl ? bodyEl.parentNode : null;
    var bodyHomeNextSibling = bodyEl ? bodyEl.nextSibling : null;
    var inMobileSearchMode = false;

    var boundInputs = [];
    var debounceTimer = null;
    var abortController = null;
    var lastQuery = '';

    drawer.style.display = '';

    function isDesktop() {
      return window.matchMedia('(min-width: 990px)').matches;
    }

    function getMobileMenuPanel() {
      return document.querySelector('.oddit-header__mobile-menu-panel');
    }

    function getMobileMenuHost() {
      return document.querySelector('[data-mobile-menu-search-host]');
    }

    function enterMobileSearchMode() {
      if (inMobileSearchMode || !bodyEl) return;
      var host = getMobileMenuHost();
      var panel = getMobileMenuPanel();
      if (!host || !panel) return;
      host.appendChild(bodyEl);
      panel.classList.add('is-searching');
      inMobileSearchMode = true;
    }

    function exitMobileSearchMode() {
      if (!inMobileSearchMode || !bodyEl) return;
      var panel = getMobileMenuPanel();
      if (panel) panel.classList.remove('is-searching');
      // Restore body to its original position inside the search drawer panel
      // so a subsequent desktop session (or re-entry into mobile search) finds
      // it where it expects.
      if (bodyHomeParent) {
        if (bodyHomeNextSibling && bodyHomeNextSibling.parentNode === bodyHomeParent) {
          bodyHomeParent.insertBefore(bodyEl, bodyHomeNextSibling);
        } else {
          bodyHomeParent.appendChild(bodyEl);
        }
      }
      inMobileSearchMode = false;
    }

    function resetSearchState() {
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      if (abortController) abortController.abort();
      lastQuery = '';
      boundInputs.forEach(function (el) { el.value = ''; });
      setState('idle');
    }

    function updateDesktopOffset() {
      if (!isDesktop()) {
        drawer.style.removeProperty('--oddit-search-drawer-top');
        return;
      }
      var headerEl = document.querySelector('.oddit-header');
      if (!headerEl) return;
      var bottom = headerEl.getBoundingClientRect().bottom;
      drawer.style.setProperty('--oddit-search-drawer-top', Math.max(0, bottom) + 'px');
    }

    function getFocusable() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
        function (el) {
          return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        }
      );
    }

    function trapFocus(e) {
      if (e.key !== 'Tab' || !isOpen) return;
      var focusable = getFocusable();
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocused = document.activeElement;
      updateDesktopOffset();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(function () {
        // Only focus the drawer's own input when it's actually visible (mobile);
        // on desktop the header's search input remains the active field.
        if (input && input.offsetParent !== null) {
          try { input.focus(); } catch (e) {}
        }
      }, 50);
      // If an input already has a value (e.g. coming from /search?q=...), kick off a search.
      var preset = currentQuery();
      if (preset) handleQuery(preset, { immediate: true });
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      // Suppress the public open() briefly so that restoring focus to the
      // trigger input (whose focus listener calls OdditSearchDrawer.open())
      // doesn't immediately reopen the drawer.
      if (window.OdditSearchDrawer) {
        window.OdditSearchDrawer._suppressOpen = true;
      }
      // If the mobile menu was the layer behind us, close it too so the user
      // doesn't land back in it after dismissing the search.
      if (window.OdditMobileMenu && window.OdditMobileMenu.isOpen()) {
        window.OdditMobileMenu.close();
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (e) {}
      }
      setTimeout(function () {
        if (window.OdditSearchDrawer) {
          window.OdditSearchDrawer._suppressOpen = false;
        }
      }, 150);
    }

    function toggle() {
      if (isOpen) close(); else open();
    }

    // ---------- Predictive search ----------

    function currentQuery() {
      for (var i = 0; i < boundInputs.length; i++) {
        if (boundInputs[i].value) return boundInputs[i].value;
      }
      return '';
    }

    function syncInputs(value, except) {
      boundInputs.forEach(function (el) {
        if (el !== except && el.value !== value) el.value = value;
      });
    }

    function renderProducts(products) {
      if (!productsEl) return;
      if (!products.length) {
        productsEl.innerHTML = '';
        return;
      }
      productsEl.innerHTML = products.map(function (p) {
        var img = (p.featured_image && p.featured_image.url) || p.image || '';
        var alt = (p.featured_image && p.featured_image.alt) || p.title || '';
        return (
          '<a class="oddit-search-drawer__product-card" href="' + escapeHtml(p.url) + '">' +
            '<div class="oddit-search-drawer__product-image">' +
              (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(alt) + '" loading="lazy">' : '') +
            '</div>' +
            '<div class="oddit-search-drawer__product-info">' +
              '<p class="oddit-search-drawer__product-title">' + escapeHtml(p.title || '') + '</p>' +
              (p.price ? '<p class="oddit-search-drawer__product-price">' + escapeHtml(p.price) + '</p>' : '') +
            '</div>' +
          '</a>'
        );
      }).join('');
    }

    function setState(state, query) {
      query = query || '';
      if (state === 'idle') {
        drawer.classList.remove('is-searching');
        if (bodyEl) bodyEl.classList.remove('is-searching');
        if (resultsLabel) resultsLabel.style.display = 'none';
        if (loadingEl) loadingEl.style.display = 'none';
        if (emptyEl) emptyEl.style.display = 'none';
        if (idleEl) idleEl.style.display = '';
        if (productsEl) productsEl.innerHTML = '';
        return;
      }
      drawer.classList.add('is-searching');
      if (bodyEl) bodyEl.classList.add('is-searching');
      if (idleEl) idleEl.style.display = 'none';
      if (queryEl) queryEl.textContent = query;
      if (resultsLabel) resultsLabel.style.display = '';
      if (state === 'loading') {
        if (loadingEl) loadingEl.style.display = '';
        if (emptyEl) emptyEl.style.display = 'none';
        return;
      }
      if (loadingEl) loadingEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = state === 'empty' ? '' : 'none';
    }

    function fetchSearch(query) {
      if (abortController) abortController.abort();
      abortController = new AbortController();
      // Always request the API max (10) and cap client-side. Asking the API for the
      // configured resultsLimit directly returns too few products when multiple
      // resource types share the same limit budget.
      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
        '&resources[type]=' + encodeURIComponent(resourceTypes) +
        '&resources[limit]=10' +
        '&resources[limit_scope]=each';
      setState('loading', query);
      return fetch(url, {
        headers: { 'Accept': 'application/json' },
        signal: abortController.signal
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (query !== lastQuery) return; // stale
          var results = (data && data.resources && data.resources.results) || {};
          var products = (results.products || []).slice(0, resultsLimit);
          renderProducts(products);
          setState(products.length ? 'results' : 'empty', query);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (query !== lastQuery) return;
          setState('empty', query);
        });
    }

    function handleQuery(query, opts) {
      opts = opts || {};
      query = (query || '').trim();
      lastQuery = query;
      if (!query) {
        if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
        if (abortController) abortController.abort();
        setState('idle');
        return;
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      if (opts.immediate) {
        fetchSearch(query);
      } else {
        debounceTimer = setTimeout(function () { fetchSearch(query); }, DEBOUNCE_MS);
      }
    }

    function bindInput(el) {
      if (!el || el.__odditSearchBound) return;
      el.__odditSearchBound = true;
      boundInputs.push(el);
      // Focus enters mobile search mode immediately so the user sees popular
      // searches + featured product before they type. Once entered, the user
      // exits by closing the mobile menu (clearing the query keeps them in
      // the idle search view rather than snapping back to browse).
      el.addEventListener('focus', function () {
        if (isDesktop()) return;
        enterMobileSearchMode();
      });
      el.addEventListener('input', function () {
        syncInputs(el.value, el);
        if (isDesktop()) {
          if (!isOpen && el.value) open();
        } else if (el.value) {
          enterMobileSearchMode();
        }
        handleQuery(el.value);
      });
    }

    function bindForm(form) {
      if (!form || form.__odditSearchFormBound) return;
      form.__odditSearchFormBound = true;
      form.addEventListener('submit', function (e) {
        // Don't navigate to /search; keep the user in the live-results UI.
        e.preventDefault();
        var inputEl = form.querySelector('input[name="q"]');
        var value = inputEl ? inputEl.value : '';
        syncInputs(value, inputEl);
        if (isDesktop()) {
          if (!isOpen) open();
        } else if (value) {
          enterMobileSearchMode();
        }
        handleQuery(value, { immediate: true });
      });
    }

    HEADER_INPUT_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), bindInput);
    });
    if (input) bindInput(input);

    HEADER_FORM_SELECTORS.forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), bindForm);
    });
    var drawerForm = drawer.querySelector('.oddit-search-drawer__form');
    if (drawerForm) bindForm(drawerForm);

    // ---------- Event listeners ----------

    if (overlay) overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
      else if (isOpen) trapFocus(e);
    });

    // Close on any click outside the drawer panel and outside the header search input.
    // The header input acts as the search field on desktop, so clicks on it must
    // not close the drawer.
    document.addEventListener('mousedown', function (e) {
      if (!isOpen) return;
      var panel = drawer.querySelector('.oddit-search-drawer__panel');
      var headerSearch = document.querySelector('.oddit-header__search');
      if (panel && panel.contains(e.target)) return;
      if (headerSearch && headerSearch.contains(e.target)) return;
      close();
    });

    // When the mobile menu closes, tear down search mode and put the body back
    // in the search drawer so the next session (mobile or desktop) starts clean.
    document.addEventListener('mobile-menu:close', function () {
      if (!inMobileSearchMode) return;
      exitMobileSearchMode();
      resetSearchState();
    });

    // The mobile menu's close button acts as a "back" affordance while the
    // user is in search mode — exit search mode without closing the menu.
    document.addEventListener('mobile-menu:exit-search', function () {
      if (!inMobileSearchMode) return;
      exitMobileSearchMode();
      resetSearchState();
      var menuInput = document.querySelector('.oddit-header__mobile-menu-search-input');
      if (menuInput) {
        try { menuInput.blur(); } catch (e) {}
      }
    });

    var wasDesktop = isDesktop();
    window.addEventListener('resize', function () {
      if (isOpen) updateDesktopOffset();
      var nowDesktop = isDesktop();
      if (nowDesktop !== wasDesktop) {
        wasDesktop = nowDesktop;
        // Crossing the breakpoint mid-search: bail out of mobile mode and
        // close the desktop drawer if it happens to be open. The user's query
        // is cleared — simpler than reconciling layouts mid-flight.
        if (inMobileSearchMode) {
          exitMobileSearchMode();
          resetSearchState();
        }
        if (nowDesktop && isOpen) {
          // No-op: desktop panel keeps working; offset already updated above.
        } else if (!nowDesktop && isOpen) {
          close();
        }
      }
    });

    var instance = {
      el: drawer,
      open: open,
      close: close,
      toggle: toggle,
      destroy: function () {
        drawer.__odditSearchInit = false;
      }
    };
    instances.push(instance);
    return instance;
  }

  function firstInstance() {
    return instances[0] || null;
  }

  window.OdditSearchDrawer = {
    _suppressOpen: false,
    open: function () {
      if (this._suppressOpen) return;
      var i = firstInstance(); if (i) i.open();
    },
    close: function () { var i = firstInstance(); if (i) i.close(); },
    toggle: function () { var i = firstInstance(); if (i) i.toggle(); },
    instances: instances
  };

  function initAll(root) {
    var scope = root || document;
    var drawers = scope.querySelectorAll('.oddit-search-drawer');
    Array.prototype.forEach.call(drawers, function (d) {
      if (!d.__odditSearchInit) createDrawer(d);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    if (e.target.matches && e.target.matches('.oddit-search-drawer')) {
      createDrawer(e.target);
    } else {
      initAll(e.target);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    for (var i = instances.length - 1; i >= 0; i--) {
      var el = instances[i].el;
      if (el === e.target || (e.target.contains && e.target.contains(el))) {
        instances[i].destroy();
        instances.splice(i, 1);
      }
    }
  });
})();
